import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Button, Modal, Form, Alert,
  Badge, Row, Col, InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faEdit, faTrashAlt, faTimes,
  faSave, faPalette, faTags
} from '@fortawesome/free-solid-svg-icons';
import { api } from '../../config/api';

const LabelList = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editLabel, setEditLabel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#6c757d'
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Predefined colors
  const colorOptions = [
    '#4CAF50', '#2196F3', '#9C27B0', '#FF5722', '#FF9800',
    '#E91E63', '#00BCD4', '#795548', '#607D8B', '#3F51B5',
    '#F44336', '#009688', '#FFEB3B', '#8BC34A', '#673AB7'
  ];
  
  // Fetch labels
  useEffect(() => {
    const fetchLabels = async () => {
      try {
        setLoading(true);
        const response = await api.get('/labels');
        setLabels(response.data.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching labels:', err);
        setError('Failed to load labels. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchLabels();
  }, []);
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Open modal for creating a new label
  const handleOpenCreateModal = () => {
    setEditLabel(null);
    setFormData({
      name: '',
      description: '',
      color: '#6c757d'
    });
    setFormErrors({});
    setShowModal(true);
  };
  
  // Open modal for editing a label
  const handleOpenEditModal = (label) => {
    setEditLabel(label);
    setFormData({
      name: label.name,
      description: label.description || '',
      color: label.color || '#6c757d'
    });
    setFormErrors({});
    setShowModal(true);
  };
  
  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (formData.name.length > 30) {
      errors.name = 'Name cannot exceed 30 characters';
    }
    
    if (formData.description.length > 100) {
      errors.description = 'Description cannot exceed 100 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Save label (create or update)
  const handleSaveLabel = async () => {
    if (!validateForm()) return;
    
    try {
      let response;
      
      if (editLabel) {
        // Update existing label
        response = await api.put(`/labels/${editLabel.id}`, formData);
        
        // Update labels state
        if (response.data.success) {
          setLabels(prevLabels => 
            prevLabels.map(label => 
              label.id === editLabel.id ? response.data.data : label
            )
          );
        }
      } else {
        // Create new label
        response = await api.post('/labels', formData);
        
        // Update labels state
        if (response.data.success) {
          setLabels(prevLabels => [...prevLabels, response.data.data]);
        }
      }
      
      // Close modal on success
      if (response.data.success) {
        setShowModal(false);
      } else {
        setFormErrors({ general: response.data.message || 'Failed to save label' });
      }
    } catch (err) {
      console.error('Error saving label:', err);
      setFormErrors({ 
        general: err.response?.data?.message || 'An error occurred while saving the label' 
      });
    }
  };
  
  // Delete label
  const handleDeleteLabel = async (labelId) => {
    if (!window.confirm('Are you sure you want to delete this label?')) {
      return;
    }
    
    try {
      const response = await api.delete(`/labels/${labelId}`);
      
      if (response.data.success) {
        // Remove from state
        setLabels(prevLabels => 
          prevLabels.filter(label => label.id !== labelId)
        );
      } else {
        alert(response.data.message || 'Failed to delete label');
      }
    } catch (err) {
      console.error('Error deleting label:', err);
      alert('An error occurred while deleting the label');
    }
  };

  if (loading && labels.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading labels...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="my-3">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  return (
    <div className="labels-list">
      <div className="page-header">
        <div>
          <p className="eyebrow">Organize</p>
          <h1>Labels</h1>
          <p className="text-muted mb-0">Tag expenses across categories for filtering.</p>
        </div>
        <Button variant="primary" onClick={handleOpenCreateModal}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add label
        </Button>
      </div>
      
      <div className="info-callout mb-4">
        <div className="d-flex align-items-center mb-2">
          <FontAwesomeIcon icon={faTags} className="me-2 text-primary" />
          <h5 className="mb-0">About labels</h5>
        </div>
        <p className="mb-0 text-muted">
          Labels help you organize and filter expenses across categories.
          Add multiple labels to any expense — for example Recurring, Holiday, or Shared trip.
        </p>
      </div>
      
      <Card className="shadow-sm mb-4">
        <Table responsive hover className="mb-0">
          <thead>
            <tr>
              <th style={{ width: '50px' }}>Color</th>
              <th>Name</th>
              <th>Description</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {labels.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center py-4">
                  No labels found. Create your first label to get started!
                </td>
              </tr>
            ) : (
              labels.map(label => (
                <tr key={label.id}>
                  <td>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        backgroundColor: label.color || '#6c757d',
                        marginRight: '8px',
                        verticalAlign: 'middle',
                        border: '1px solid #ccc'
                      }}
                    ></span>
                  </td>
                  <td>{label.name}</td>
                  <td className="text-muted">{label.description}</td>
                  <td className="text-end">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleOpenEditModal(label)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteLabel(label.id)}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>
      
      {/* Label Edit/Create Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editLabel ? 'Edit Label' : 'Add Label'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formErrors.general && (
            <Alert variant="danger" className="mb-3">
              {formErrors.general}
            </Alert>
          )}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control 
                type="text" 
                name="name" 
                value={formData.name}
                onChange={handleInputChange}
                isInvalid={!!formErrors.name}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={2} 
                name="description" 
                value={formData.description}
                onChange={handleInputChange}
                isInvalid={!!formErrors.description}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.description}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Color</Form.Label>
              <div className="mb-3">
                <Row className="g-2">
                  {colorOptions.map(color => (
                    <Col key={color} xs={3}>
                      <Button
                        style={{ 
                          backgroundColor: color, 
                          borderColor: formData.color === color ? '#000' : color 
                        }}
                        className="w-100 p-3"
                        onClick={() => setFormData({ ...formData, color })}
                      >
                        {formData.color === color && (
                          <FontAwesomeIcon icon="check" color="#fff" />
                        )}
                      </Button>
                    </Col>
                  ))}
                </Row>
              </div>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faPalette} />
                </InputGroup.Text>
                <Form.Control 
                  type="color" 
                  name="color" 
                  value={formData.color}
                  onChange={handleInputChange}
                />
                <Form.Control 
                  type="text" 
                  name="color" 
                  value={formData.color}
                  onChange={handleInputChange}
                />
              </InputGroup>
            </Form.Group>
          </Form>
          
            <div className="mt-4">
            <h6>Preview</h6>
            <div className="info-callout d-flex align-items-center">
              <Badge 
                pill 
                style={{ 
                  backgroundColor: formData.color,
                  marginRight: '1rem',
                  padding: '0.5rem 1rem'
                }}
              >
                {formData.name || 'Label Name'}
              </Badge>
              {formData.description && (
                <div className="small text-muted">{formData.description}</div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveLabel}>
            <FontAwesomeIcon icon={faSave} className="me-2" />
            {editLabel ? 'Update Label' : 'Save Label'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LabelList;
